/**
 * Friendly copy for `POST /auth/signup` error codes.
 *
 * Shared by BOTH signup surfaces — the standalone `/signup` page and the
 * in-storefront register dialog at checkout — so a registration gate
 * (`signup_mode`) reads the same way wherever the buyer hits it. Before this
 * was shared, the checkout dialog handled none of the gate codes and showed the
 * useless "Could not create account. Please try again." for a 403 that actually
 * means "you need an invite link".
 */
export function prettyError(
  code?: string,
  /**
   * Set when the visitor arrived through a campaign page (`?c=<slug>`). Only the
   * server can tell whether that slug resolves to an ENABLED page, so a gate code
   * coming back on a campaign visit means the campaign itself is stale — say
   * that, instead of asking for an invite link they thought they had.
   */
  opts?: { viaCampaign?: boolean },
): string {
  if (opts?.viaCampaign && (code === 'signup_requires_referral_link' || code === 'signup_requires_sponsor')) {
    return 'This campaign link is no longer active — please ask your sponsor for a new one.';
  }
  switch (code) {
    case 'email_taken':
      return 'That email is already registered. Try signing in.';
    case 'unknown_sponsor_code':
      return 'Affiliate code not recognized.';
    case 'signup_requires_referral_link':
      return 'Registration is available through referral links only. Please use your sponsor’s invite link.';
    case 'signup_requires_sponsor':
      return 'An affiliate code is required to sign up.';
    case 'default_sponsor_unavailable':
      return 'System is not ready for signups yet. Please contact support.';
    case 'terms_not_accepted':
      return 'Please accept the Terms and Privacy Policy to continue.';
    case 'terms_version_mismatch':
      return 'The Terms were just updated. Please reload the page and try again.';
    case 'username_taken':
      return 'That username is taken. Please choose another.';
    case 'username_required':
      return 'Please choose a username to continue.';
    default:
      return 'Could not create account. Please try again.';
  }
}
